import { useCallback, useState } from 'react';
import { Plus, ExternalLink, CheckCircle2, Check, X, Link2, Home, Droplets, TreePine, RefreshCw } from 'lucide-react';
import { ds } from '../tokens';
import { DateField, Picker, SegmentedTabs, TextField, Tooltip } from '../primitives';
import type { ImportantDate, ImportantDateLink } from '../../shared/types/profile';

// ── Suggestion data ─────────────────────────────────────────

type MaintenanceCategory = 'indoor' | 'plumbing' | 'exterior';
type GroupBy = 'category' | 'frequency';

const CATEGORY_META: Record<MaintenanceCategory, { label: string; icon: typeof Home }> = {
  indoor:   { label: 'Indoor',   icon: Home },
  plumbing: { label: 'Plumbing', icon: Droplets },
  exterior: { label: 'Exterior', icon: TreePine },
};

const CATEGORY_ORDER: MaintenanceCategory[] = ['indoor', 'plumbing', 'exterior'];

const FREQUENCY_ORDER = ['Every month', 'Every 3 months', 'Every 6 months', 'Annually', 'Every 3 years'];

const GROUP_BY_TABS = [
  { id: 'category' as const, label: 'Category' },
  { id: 'frequency' as const, label: 'Frequency' },
];

interface MaintenanceSuggestion {
  label: string;
  frequency: string;
  tip: string;
  defaultMonths: number;
  category: MaintenanceCategory;
  showFor: ('owned' | 'rented')[];
}

const SUGGESTIONS: MaintenanceSuggestion[] = [
  { label: 'HVAC filter change', frequency: 'Every 3 months', tip: 'Replace air filters to improve air quality and reduce energy costs.', defaultMonths: 3, category: 'indoor', showFor: ['owned', 'rented'] },
  { label: 'Smoke detector battery', frequency: 'Every 6 months', tip: 'Test all smoke and CO detectors. Replace batteries even if they still work.', defaultMonths: 6, category: 'indoor', showFor: ['owned', 'rented'] },
  { label: 'Dryer vent cleaning', frequency: 'Annually', tip: 'Clean lint buildup from the full vent line to prevent fire hazards.', defaultMonths: 12, category: 'indoor', showFor: ['owned', 'rented'] },
  { label: 'Hot water heater flush', frequency: 'Every 6 months', tip: 'Drain sediment to extend tank life and maintain heating efficiency.', defaultMonths: 6, category: 'plumbing', showFor: ['owned'] },
  { label: 'Water heater inspection', frequency: 'Annually', tip: 'Check the anode rod and pressure relief valve for corrosion or wear.', defaultMonths: 12, category: 'plumbing', showFor: ['owned'] },
  { label: 'Septic tank pumping', frequency: 'Every 3 years', tip: 'Pump to prevent backups. Frequency depends on household size.', defaultMonths: 36, category: 'plumbing', showFor: ['owned'] },
  { label: 'Gutter cleaning', frequency: 'Every 6 months', tip: 'Clear gutters and downspouts in spring and fall to prevent water damage.', defaultMonths: 6, category: 'exterior', showFor: ['owned'] },
  { label: 'Roof inspection', frequency: 'Annually', tip: 'Look for damaged shingles, flashing, or leaks before the rainy season.', defaultMonths: 12, category: 'exterior', showFor: ['owned'] },
  { label: 'Winterize sprinklers', frequency: 'Annually', tip: 'Blow out irrigation lines before the first freeze to prevent pipe damage.', defaultMonths: 12, category: 'exterior', showFor: ['owned'] },
  { label: 'Pest control treatment', frequency: 'Every 3 months', tip: 'Preventive perimeter spray keeps insects and rodents out.', defaultMonths: 3, category: 'exterior', showFor: ['owned'] },
];

/** Label → frequency lookup for backfilling existing data that predates the frequency field */
export const MAINTENANCE_FREQUENCY: ReadonlyMap<string, string> = new Map(
  SUGGESTIONS.map(s => [s.label, s.frequency])
);

const STALE_FREQUENCY: ReadonlyMap<string, string> = new Map([
  ['Every 1–3 months', 'Every 3 months'],
  ['Every 6–12 months', 'Every 6 months'],
  ['Twice a year', 'Every 6 months'],
  ['Quarterly', 'Every 3 months'],
  ['Annually (fall)', 'Annually'],
  ['Every 3–5 years', 'Every 3 years'],
]);

/** Resolve frequency: normalize stale values, backfill from known labels, or return undefined */
export function resolveFrequency(label: string, stored?: string): string | undefined {
  if (stored) return STALE_FREQUENCY.get(stored) ?? stored;
  return MAINTENANCE_FREQUENCY.get(label);
}

// ── Component ───────────────────────────────────────────────

interface MaintenanceSuggestionsProps {
  ownership: 'owned' | 'rented';
  dates: ImportantDate[];
  onDatesChange: (dates: ImportantDate[]) => void;
  showCustomForm?: boolean;
  onCustomFormChange?: (show: boolean) => void;
}

function defaultDate(months: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return d.toISOString().split('T')[0];
}

// ── Link list sub-component ────────────────────────────────

interface LinkListProps {
  links: ImportantDateLink[];
  onUpdate: (links: ImportantDateLink[]) => void;
}

function LinkList({ links, onUpdate }: LinkListProps) {
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newUrl, setNewUrl] = useState('');

  const handleAdd = useCallback(() => {
    const label = newLabel.trim() || 'How to';
    let url = newUrl.trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) url = 'https://' + url;
    const link: ImportantDateLink = {
      id: `lnk_${Date.now()}_${Math.random().toString(36).slice(2, 5)}`,
      label,
      url,
    };
    onUpdate([...links, link]);
    setNewLabel('');
    setNewUrl('');
    setAdding(false);
  }, [newLabel, newUrl, links, onUpdate]);

  const handleDelete = useCallback((id: string) => {
    onUpdate(links.filter(l => l.id !== id));
  }, [links, onUpdate]);

  return (
    <div className="mt-1.5 space-y-1">
      <div className="flex items-center gap-3 flex-wrap">
        {links.map(l => (
          <div key={l.id} className="flex items-center gap-1 group">
            <a
              href={l.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-1 ${ds.type.footnote} ${ds.type.weight.medium} truncate`}
              style={{ color: ds.color.raw.systemBlue }}
              onClick={e => e.stopPropagation()}
            >
              <ExternalLink className={ds.icon.xs} /> {l.label}
            </a>
            <Tooltip text="Remove link" position="top">
              <button
                type="button"
                onClick={() => handleDelete(l.id)}
                className={`opacity-0 group-hover:opacity-100 ${ds.transition.fast} flex items-center justify-center hover:scale-110`}
                style={{ color: ds.color.raw.systemGray3 }}
                onMouseEnter={e => { e.currentTarget.style.color = ds.color.raw.systemRed; }}
                onMouseLeave={e => { e.currentTarget.style.color = ds.color.raw.systemGray3; }}
                aria-label={`Remove ${l.label}`}
              >
                <X className={ds.icon.xs} />
              </button>
            </Tooltip>
          </div>
        ))}
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className={`inline-flex items-center gap-1 px-3 py-1 ${ds.radius.full} ${ds.type.footnote} ${ds.type.weight.medium} ${ds.transition.standard} border`}
            style={{ color: ds.color.raw.systemBlue, borderColor: ds.color.raw.systemBlue, backgroundColor: 'transparent' }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = ds.color.raw.systemBlue; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = ds.color.raw.systemBlue; }}
          >
            <Link2 className={ds.icon.xs} /> Add link
          </button>
        )}
      </div>

      {adding ? (
        <div className="flex items-end gap-2 mt-1">
          <TextField
            label="Label"
            value={newLabel}
            onChange={e => setNewLabel(e.target.value)}
            placeholder="How to"
            maxLength={40}
            className="flex-[2] min-w-0"
          />
          <TextField
            label="URL"
            value={newUrl}
            onChange={e => setNewUrl(e.target.value)}
            placeholder="https://..."
            autoFocus
            className="flex-[5] min-w-0"
          />
          <Tooltip text="Add" position="top">
            <button
              type="button"
              onClick={handleAdd}
              className={`flex items-center justify-center ${ds.icon.buttonMd} rounded-md ${ds.transition.fast} mb-0.5`}
              aria-label="Add link"
            >
              <Check className={`${ds.icon.sm} ${ds.transition.fast} hover:scale-110`} style={{ color: ds.color.raw.systemGreen }} />
            </button>
          </Tooltip>
          <Tooltip text="Cancel" position="top">
            <button
              type="button"
              onClick={() => { setAdding(false); setNewLabel(''); setNewUrl(''); }}
              className={`flex items-center justify-center ${ds.icon.buttonMd} rounded-md ${ds.transition.fast} mb-0.5`}
              aria-label="Cancel"
            >
              <X className={`${ds.icon.sm} ${ds.transition.fast} hover:scale-110`} style={{ color: ds.color.raw.systemGray }} />
            </button>
          </Tooltip>
        </div>
      ) : null}
    </div>
  );
}

// ── Frequency options for custom items ─────────────────────

const FREQUENCY_OPTIONS = [
  'Every month',
  'Every 3 months',
  'Every 6 months',
  'Annually',
];

// ── Main component ─────────────────────────────────────────

export function MaintenanceSuggestions({ ownership, dates, onDatesChange, showCustomForm, onCustomFormChange }: MaintenanceSuggestionsProps) {
  const suggestions = SUGGESTIONS.filter(s => s.showFor.includes(ownership));
  const isAdded = useCallback((label: string) => dates.some(d => d.label === label), [dates]);
  const [editing, setEditing] = useState<string | null>(null);
  const [editDate, setEditDate] = useState('');
  const [groupBy, setGroupBy] = useState<GroupBy>('category');

  // Custom maintenance form state — controlled externally or internally
  const [internalShowCustom, setInternalShowCustom] = useState(false);
  const showCustom = showCustomForm ?? internalShowCustom;
  const setShowCustom = onCustomFormChange ?? setInternalShowCustom;
  const [customLabel, setCustomLabel] = useState('');
  const [customDate, setCustomDate] = useState('');
  const [customFrequency, setCustomFrequency] = useState('');

  const startAdd = useCallback((s: MaintenanceSuggestion) => {
    setEditing(s.label);
    setEditDate(defaultDate(s.defaultMonths));
  }, []);

  const confirmAdd = useCallback((s: MaintenanceSuggestion) => {
    if (!editDate) return;
    const entry: ImportantDate = {
      id: `id_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      label: s.label,
      date: editDate,
      frequency: s.frequency,
    };
    onDatesChange([...dates, entry]);
    setEditing(null);
    setEditDate('');
  }, [editDate, dates, onDatesChange]);

  const cancelAdd = useCallback(() => {
    setEditing(null);
    setEditDate('');
  }, []);

  const updateLinks = useCallback((label: string, links: ImportantDateLink[]) => {
    onDatesChange(dates.map(d => d.label === label ? { ...d, links } : d));
  }, [dates, onDatesChange]);

  const addCustom = useCallback(() => {
    const label = customLabel.trim();
    if (!label || !customDate) return;
    const entry: ImportantDate = {
      id: `id_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      label,
      date: customDate,
      frequency: customFrequency || undefined,
    };
    onDatesChange([...dates, entry]);
    setCustomLabel('');
    setCustomDate('');
    setCustomFrequency('');
    setShowCustom(false);
  }, [customLabel, customDate, customFrequency, dates, onDatesChange]);

  // Build groups based on selected mode
  const groups: { key: string; label: string; icon: typeof Home; items: MaintenanceSuggestion[] }[] =
    groupBy === 'category'
      ? CATEGORY_ORDER
          .map(cat => ({ key: cat, label: CATEGORY_META[cat].label, icon: CATEGORY_META[cat].icon, items: suggestions.filter(s => s.category === cat) }))
          .filter(g => g.items.length > 0)
      : FREQUENCY_ORDER
          .map(freq => ({ key: freq, label: freq, icon: RefreshCw, items: suggestions.filter(s => s.frequency === freq) }))
          .filter(g => g.items.length > 0);

  const renderItem = (s: MaintenanceSuggestion, isLast: boolean) => {
    const added = isAdded(s.label);
    const isEditingThis = editing === s.label;
    const addedDate = added ? dates.find(d => d.label === s.label) : null;
    return (
      <div
        key={s.label}
        className={`px-4 py-3 ${ds.transition.fast} hover:bg-black/[0.02]`}
        style={{
          ...(added ? { opacity: 0.75 } : undefined),
          ...(!isLast ? { borderBottom: `0.5px solid ${ds.color.raw.opaqueSeparator}` } : undefined),
        }}
      >
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className={`${ds.type.body} ${ds.type.weight.medium} ${ds.color.label.primary}`}>
                {s.label}
              </p>
              <span
                className={ds.color.chip.categoryBadge}
                style={{ backgroundColor: ds.color.chip.blue.bg, color: ds.color.chip.blue.text }}
              >
                {groupBy === 'category' ? s.frequency : CATEGORY_META[s.category].label}
              </span>
            </div>
            <p className={`${ds.type.footnote} ${ds.color.label.secondary} mt-1`}>
              {s.tip}
            </p>

            {isEditingThis && (
              <div className="flex items-center gap-2 mt-2">
                <DateField
                  label="Next due date"
                  value={editDate}
                  onChange={setEditDate}
                  allowFuture
                  className="flex-1"
                />
                <Tooltip text="Confirm" position="top">
                  <button
                    type="button"
                    onClick={() => confirmAdd(s)}
                    className={`flex items-center justify-center ${ds.icon.buttonMd} rounded-md ${ds.transition.fast}`}
                    aria-label="Confirm add"
                  >
                    <Check className={ds.icon.sm} style={{ color: ds.color.raw.systemGreen }} />
                  </button>
                </Tooltip>
                <Tooltip text="Cancel" position="top">
                  <button
                    type="button"
                    onClick={cancelAdd}
                    className={`flex items-center justify-center ${ds.icon.buttonMd} rounded-md ${ds.transition.fast}`}
                    aria-label="Cancel"
                  >
                    <X className={ds.icon.sm} style={{ color: ds.color.raw.systemGray }} />
                  </button>
                </Tooltip>
              </div>
            )}
          </div>
          <div className="flex-shrink-0 pt-0.5">
            {added ? (
              <span className="flex items-center gap-1" style={{ color: ds.color.raw.systemGreen }}>
                <CheckCircle2 className={ds.icon.sm} />
                <span className={`${ds.type.footnote} ${ds.type.weight.medium}`}>Added</span>
              </span>
            ) : !isEditingThis ? (
              <Tooltip text="Add as reminder" position="top">
                <button
                  type="button"
                  onClick={() => startAdd(s)}
                  className={`flex items-center gap-1 px-2.5 py-1 ${ds.radius.full} ${ds.type.footnote} ${ds.type.weight.medium} ${ds.transition.fast}`}
                  style={{ color: ds.color.raw.systemBlue, backgroundColor: ds.color.raw.systemBlue + '0D' }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = ds.color.raw.systemBlue + '1A'; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = ds.color.raw.systemBlue + '0D'; }}
                >
                  <Plus className={ds.icon.footer} />
                  Add
                </button>
              </Tooltip>
            ) : null}
          </div>
        </div>
        {added && addedDate && (
          <LinkList
            links={addedDate.links || []}
            onUpdate={(links) => updateLinks(s.label, links)}
          />
        )}
      </div>
    );
  };

  return (
    <div className="space-y-1.5">
      <SegmentedTabs
        tabs={GROUP_BY_TABS}
        activeTab={groupBy}
        onTabChange={(id) => setGroupBy(id as GroupBy)}
        className="mb-1"
      />

      {groups.map(({ key, label, icon: Icon, items }) => (
        <div key={key}>
          <div className="flex items-center gap-1.5 px-1 pt-2 pb-1">
            <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: ds.color.raw.systemBlue }} />
            <span className={`${ds.type.body} ${ds.type.weight.semibold} ${ds.color.label.primary}`}>
              {label}
            </span>
          </div>
          <div className={ds.layout.insetCard}>
            {items.map((s, i) => renderItem(s, i === items.length - 1))}
          </div>
        </div>
      ))}

      {/* Custom reminder form */}
      {showCustom ? (
        <div className={`${ds.layout.insetCard} px-4 py-3`}>
          <p className={`${ds.type.body} ${ds.type.weight.medium} ${ds.color.label.primary} mb-2`}>
            Add reminder
          </p>
          <div className="space-y-2">
            <TextField
              label="Task name"
              value={customLabel}
              onChange={e => setCustomLabel(e.target.value)}
              maxLength={60}
              autoFocus
            />
            <div className="flex items-start gap-2">
              <DateField
                label="Next due date"
                value={customDate}
                onChange={setCustomDate}
                allowFuture
                className="flex-1"
              />
              <Picker
                label="Frequency"
                value={customFrequency}
                onChange={setCustomFrequency}
                options={[
                  { value: '', label: 'One-time' },
                  ...FREQUENCY_OPTIONS.map(f => ({ value: f, label: f })),
                ]}
                className="flex-1"
              />
            </div>
            <div className="flex items-center justify-end gap-3 pt-1">
              <button
                type="button"
                onClick={() => { setShowCustom(false); setCustomLabel(''); setCustomDate(''); setCustomFrequency(''); }}
                className={`${ds.type.body} ${ds.transition.fast} px-3 py-1.5 ${ds.radius.full} hover:bg-gray-100`}
                style={{ color: ds.color.raw.systemGray }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={addCustom}
                className={`${ds.type.body} ${ds.type.weight.semibold} ${ds.transition.fast} px-3 py-1.5 ${ds.radius.full}`}
                style={{ color: ds.color.raw.systemBlue, backgroundColor: 'transparent' }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = ds.color.raw.systemBlue + '14'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
